import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import './DateRangePicker.css';

const DEFAULT_START_TIME = "00:00";
const DEFAULT_END_TIME = "23:59";

const parseTimeFromDate = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
  const h = date.getHours();
  const m = date.getMinutes();
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// Format Date for date input (YYYY-MM-DD)
const formatDateOnly = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Format Date for datetime-local input (YYYY-MM-DDTHH:mm)
const formatDateTimeLocal = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
};

const DateRangePicker = ({ 
  startDate, 
  endDate, 
  onChange, 
  placeholder = "Select Date Range",
  className = "",
  disabled = false,
  allowSingleDate = true,
  allowDateRange = true,
  showTimeSelection = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectionMode, setSelectionMode] = useState('range'); // 'single' or 'range'
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [tempStartTime, setTempStartTime] = useState(DEFAULT_START_TIME);
  const [tempEndTime, setTempEndTime] = useState(DEFAULT_END_TIME);
  const [currentView, setCurrentView] = useState('presets'); // 'presets' or 'custom'
  const dropdownRef = useRef(null);
  const datePickerRef = useRef(null);

  // Utility function to validate dates
  const isValidDate = (date) => {
    return date && date instanceof Date && !isNaN(date.getTime());
  };

  // Predefined date range options
  const dateRangePresets = [
    {
      label: "Today",
      value: "today",
      getDates: () => [moment().startOf('day').toDate(), moment().endOf('day').toDate()]
    },
    {
      label: "Yesterday", 
      value: "yesterday",
      getDates: () => [moment().subtract(1, 'day').startOf('day').toDate(), moment().subtract(1, 'day').endOf('day').toDate()]
    },
    {
      label: "This Week",
      value: "thisWeek", 
      getDates: () => [moment().startOf('week').toDate(), moment().endOf('week').toDate()]
    },
    {
      label: "Last Week",
      value: "lastWeek",
      getDates: () => [moment().subtract(1, 'week').startOf('week').toDate(), moment().subtract(1, 'week').endOf('week').toDate()]
    },
    {
      label: "This Month",
      value: "thisMonth",
      getDates: () => [moment().startOf('month').toDate(), moment().endOf('month').toDate()]
    },
    {
      label: "Last Month", 
      value: "lastMonth",
      getDates: () => [moment().subtract(1, 'month').startOf('month').toDate(), moment().subtract(1, 'month').endOf('month').toDate()]
    },
    {
      label: "Last 7 Days",
      value: "last7Days",
      getDates: () => [moment().subtract(6, 'days').startOf('day').toDate(), moment().endOf('day').toDate()]
    },
    {
      label: "Last 30 Days",
      value: "last30Days", 
      getDates: () => [moment().subtract(29, 'days').startOf('day').toDate(), moment().endOf('day').toDate()]
    },
    {
      label: "Last 90 Days",
      value: "last90Days",
      getDates: () => [moment().subtract(89, 'days').startOf('day').toDate(), moment().endOf('day').toDate()]
    }
  ];

  // Single date presets
  const singleDatePresets = [
    {
      label: "Today",
      value: "today",
      getDate: () => moment().startOf('day').toDate()
    },
    {
      label: "Yesterday",
      value: "yesterday", 
      getDate: () => moment().subtract(1, 'day').startOf('day').toDate()
    },
    {
      label: "This Monday",
      value: "thisMonday",
      getDate: () => moment().startOf('week').toDate()
    },
    {
      label: "Last Monday",
      value: "lastMonday",
      getDate: () => moment().subtract(1, 'week').startOf('week').toDate()
    },
    {
      label: "First of Month",
      value: "firstOfMonth",
      getDate: () => moment().startOf('month').toDate()
    },
    {
      label: "Last of Month",
      value: "lastOfMonth",
      getDate: () => moment().endOf('month').toDate()
    }
  ];

  // Initialize temp dates and times when component opens
  useEffect(() => {
    if (isOpen) {
      setTempStartDate(isValidDate(startDate) ? startDate : null);
      setTempEndDate(isValidDate(endDate) ? endDate : null);
      if (showTimeSelection) {
        setTempStartTime(parseTimeFromDate(startDate) || DEFAULT_START_TIME);
        setTempEndTime(parseTimeFromDate(endDate) || DEFAULT_END_TIME);
      }
      setCurrentView('presets');
    }
  }, [isOpen, startDate, endDate, showTimeSelection]);

  // Check if current dates match any preset
  useEffect(() => {
    if (startDate && endDate) {
      const currentStart = moment(startDate);
      const currentEnd = moment(endDate);
      
      const matchedPreset = dateRangePresets.find(preset => {
        const [presetStart, presetEnd] = preset.getDates();
        return currentStart.isSame(moment(presetStart), 'day') && 
               currentEnd.isSame(moment(presetEnd), 'day');
      });
      
      setSelectedPreset(matchedPreset ? matchedPreset.value : null);
    } else if (startDate && !endDate) {
      // Single date selection
      const currentDate = moment(startDate);
      const matchedPreset = singleDatePresets.find(preset => {
        const presetDate = preset.getDate();
        return currentDate.isSame(moment(presetDate), 'day');
      });
      setSelectedPreset(matchedPreset ? matchedPreset.value : null);
    }
  }, [startDate, endDate]);

  // Handle preset selection for date ranges
  const handleRangePresetSelect = (preset) => {
    const [newStartDate, newEndDate] = preset.getDates();
    setSelectedPreset(preset.value);
    setSelectionMode('range');
    onChange([newStartDate, newEndDate]);
    setIsOpen(false);
  };

  // Handle preset selection for single dates
  const handleSinglePresetSelect = (preset) => {
    const newDate = preset.getDate();
    setSelectedPreset(preset.value);
    setSelectionMode('single');
    onChange([newDate, null]);
    setIsOpen(false);
  };

  // Handle custom date selection
  const handleDateChange = (dates) => {
    try {
      // Validate dates parameter
      if (!dates) {
        console.warn('No dates received in handleDateChange');
        return;
      }

      if (selectionMode === 'single') {
        // For single date mode, dates should be a single date object
        if (isValidDate(dates)) {
          setTempStartDate(dates);
          setTempEndDate(null);
        } else {
          console.warn('Invalid single date received:', dates);
        }
      } else {
        // For range mode, dates should be an array [startDate, endDate]
        if (Array.isArray(dates)) {
          const [newStartDate, newEndDate] = dates;
          
          // Validate and set start date
          if (isValidDate(newStartDate)) {
            setTempStartDate(newStartDate);
          } else if (newStartDate === null) {
            setTempStartDate(null);
          }
          
          // Validate and set end date
          if (isValidDate(newEndDate)) {
            setTempEndDate(newEndDate);
          } else if (newEndDate === null) {
            setTempEndDate(null);
          }
        } else if (isValidDate(dates)) {
          // Handle case where single date is selected in range mode
          setTempStartDate(dates);
          setTempEndDate(null);
        } else {
          console.warn('Invalid date range received:', dates);
        }
      }
    } catch (error) {
      console.error('Error handling date change:', error, 'Dates:', dates);
    }
  };

  // Combine date with time (HH:mm) into a Date object
  const applyTimeToDate = (date, timeStr) => {
    if (!isValidDate(date) || !timeStr || !/^\d{1,2}:\d{2}$/.test(timeStr)) return date;
    const [h, m] = timeStr.split(":").map(Number);
    const d = new Date(date);
    d.setHours(h, m, 0, 0);
    return d;
  };

  // Apply custom selection
  const handleApplyCustomSelection = () => {
    if (selectionMode === 'single' && isValidDate(tempStartDate)) {
      setSelectedPreset(null);
      const finalStart = showTimeSelection ? applyTimeToDate(tempStartDate, tempStartTime) : tempStartDate;
      onChange([finalStart, null]);
      setIsOpen(false);
    } else if (selectionMode === 'range' && isValidDate(tempStartDate) && isValidDate(tempEndDate)) {
      setSelectedPreset(null);
      const finalStart = showTimeSelection ? applyTimeToDate(tempStartDate, tempStartTime) : tempStartDate;
      const finalEnd = showTimeSelection ? applyTimeToDate(tempEndDate, tempEndTime) : tempEndDate;
      onChange([finalStart, finalEnd]);
      setIsOpen(false);
    } else {
      console.warn('Cannot apply: Invalid date selection', {
        mode: selectionMode,
        startDate: tempStartDate,
        endDate: tempEndDate
      });
    }
  };

  // Cancel custom selection
  const handleCancelCustomSelection = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    if (showTimeSelection) {
      setTempStartTime(parseTimeFromDate(startDate) || DEFAULT_START_TIME);
      setTempEndTime(parseTimeFromDate(endDate) || DEFAULT_END_TIME);
    }
    setCurrentView('presets');
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedPreset(null);
    setTempStartDate(null);
    setTempEndDate(null);
    onChange([null, null]);
    setIsOpen(false);
  };

  // Navigate to custom view
  const handleGoToCustom = () => {
    setCurrentView('custom');
  };

  // Navigate back to presets
  const handleGoToPresets = () => {
    setCurrentView('presets');
  };

  // Format display text
  const getDisplayText = () => {
    if (selectedPreset) {
      const rangePreset = dateRangePresets.find(p => p.value === selectedPreset);
      const singlePreset = singleDatePresets.find(p => p.value === selectedPreset);
      return rangePreset ? rangePreset.label : (singlePreset ? singlePreset.label : "Custom Selection");
    }
    
    if (startDate && endDate) {
      const start = moment(startDate).format(showTimeSelection ? 'MMM DD HH:mm' : 'MMM DD');
      const end = moment(endDate).format(showTimeSelection ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY');
      return `${start} - ${end}`;
    } else if (startDate && !endDate) {
      return moment(startDate).format(showTimeSelection ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY');
    }
    
    return placeholder;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`date-range-picker-container ${className}`}>
      {/* Main Input Button */}
      <div 
        className={`date-range-input ${isOpen ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <i className="fa fa-calendar-alt me-2"></i>
        <span className="date-range-text">{getDisplayText()}</span>
        <i className={`fa fa-chevron-down ms-auto ${isOpen ? 'rotated' : ''}`}></i>
      </div>

      {/* Dropdown with Step Navigation */}
      {isOpen && !disabled && (
        <div className="date-range-dropdown" ref={dropdownRef}>
          {currentView === 'presets' ? (
            /* Presets View */
            <div className="date-range-presets">
              <div className="presets-header">
                <i className="fa fa-clock me-2"></i>
                <span>Quick Select</span>
              </div>
              
              {/* Mode Toggle */}
              <div className="mode-toggle">
                <button 
                  className={`mode-button ${selectionMode === 'single' ? 'active' : ''}`}
                  onClick={() => setSelectionMode('single')}
                  disabled={!allowSingleDate}
                >
                  <i className="fa fa-calendar-day me-1"></i>
                  Single Date
                </button>
                <button 
                  className={`mode-button ${selectionMode === 'range' ? 'active' : ''}`}
                  onClick={() => setSelectionMode('range')}
                  disabled={!allowDateRange}
                >
                  <i className="fa fa-calendar-week me-1"></i>
                  Date Range
                </button>
              </div>
              
              {/* Preset Buttons */}
              <div className="presets-grid">
                {selectionMode === 'single' ? (
                  singleDatePresets.map((preset) => (
                    <button
                      key={preset.value}
                      className={`preset-button ${selectedPreset === preset.value ? 'active' : ''}`}
                      onClick={() => handleSinglePresetSelect(preset)}
                    >
                      {preset.label}
                    </button>
                  ))
                ) : (
                  dateRangePresets.map((preset) => (
                    <button
                      key={preset.value}
                      className={`preset-button ${selectedPreset === preset.value ? 'active' : ''}`}
                      onClick={() => handleRangePresetSelect(preset)}
                    >
                      {preset.label}
                    </button>
                  ))
                )}
              </div>
              
              {/* Navigation Buttons */}
              <div className="presets-actions">
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleClearSelection}
                >
                  <i className="fa fa-times me-1"></i>
                  Clear
                </button>
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={handleGoToCustom}
                >
                  <i className="fa fa-calendar me-1"></i>
                  Custom Date
                </button>
              </div>
            </div>
          ) : (
            /* Custom Date View */
            <div className="date-range-custom">
              <div className="custom-header">
                <button 
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={handleGoToPresets}
                >
                  <i className="fa fa-arrow-left me-1"></i>
                  Back
                </button>
                <i className="fa fa-calendar me-2"></i>
                <span>Custom Date{selectionMode === 'range' ? ' Range' : ''}</span>
              </div>
              
              {/* Mode Toggle */}
              <div className="mode-toggle">
                <button 
                  className={`mode-button ${selectionMode === 'single' ? 'active' : ''}`}
                  onClick={() => setSelectionMode('single')}
                  disabled={!allowSingleDate}
                >
                  <i className="fa fa-calendar-day me-1"></i>
                  Single Date
                </button>
                <button 
                  className={`mode-button ${selectionMode === 'range' ? 'active' : ''}`}
                  onClick={() => setSelectionMode('range')}
                  disabled={!allowDateRange}
                >
                  <i className="fa fa-calendar-week me-1"></i>
                  Date Range
                </button>
              </div>
              
              {/* Custom: single date = date only (whole day); range = start/end date and time */}
              <div className="date-range-datetime-fields" ref={datePickerRef}>
                {selectionMode === 'single' ? (
                  <div className="date-range-datetime-field">
                    <label className="date-range-time-label">Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={formatDateOnly(tempStartDate)}
                      max={formatDateOnly(new Date())}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        const d = moment(val).startOf('day').toDate();
                        if (isValidDate(d) && moment(d).isSameOrBefore(moment(), 'day')) setTempStartDate(d);
                      }}
                      title="Date (whole day, no future dates)"
                    />
                  </div>
                ) : (
                  <>
                    <div className="date-range-datetime-field">
                      <label className="date-range-time-label">Start date and time</label>
                      <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={formatDateTimeLocal(tempStartDate)}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) return;
                          const d = new Date(val);
                          if (isValidDate(d)) {
                            setTempStartDate(d);
                            setTempStartTime(parseTimeFromDate(d) || DEFAULT_START_TIME);
                          }
                        }}
                        title="Start date and time"
                      />
                    </div>
                    <div className="date-range-datetime-field">
                      <label className="date-range-time-label">End date and time</label>
                      <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={formatDateTimeLocal(tempEndDate)}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) return;
                          const d = new Date(val);
                          if (isValidDate(d)) {
                            setTempEndDate(d);
                            setTempEndTime(parseTimeFromDate(d) || DEFAULT_END_TIME);
                          }
                        }}
                        title="End date and time"
                      />
                    </div>
                  </>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="date-picker-actions">
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleCancelCustomSelection}
                >
                  <i className="fa fa-times me-1"></i>
                  Cancel
                </button>
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={handleApplyCustomSelection}
                  disabled={
                    !isValidDate(tempStartDate) || 
                    (selectionMode === 'range' && !isValidDate(tempEndDate))
                  }
                >
                  <i className="fa fa-check me-1"></i>
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
